import { useEffect, useState } from "react";
import { NavLink, Navigate, Route, BrowserRouter as Router, Routes, useLocation, useNavigate } from "react-router-dom";
import { can, type Permission, type User } from "@zenforge/shared";
import { AuthContext } from "./auth-context.js";
import { ChangePassword } from "./ChangePassword.js";
import { BookBackGeneration } from "./pages/BookBackGeneration.js";
import { CollectionDetail } from "./pages/CollectionDetail.js";
import { CollectionsList } from "./pages/CollectionsList.js";
import { Dashboard } from "./pages/Dashboard.js";
import { Generation } from "./pages/Generation.js";
import { Login } from "./pages/Login.js";
import { Playground } from "./pages/Playground.js";
import { RunPipeline } from "./pages/RunPipeline.js";
import { PipelineExecutions, Queue } from "./pages/Queue.js";
import { Settings } from "./pages/Settings.js";
import { Syllabus } from "./pages/Syllabus.js";
import { SyllabusReport } from "./pages/SyllabusReport.js";
import { Users } from "./pages/Users.js";

interface Screen {
  path: string;
  label: string;
  title?: string;
  section: string;
  /** Required to see the screen. Omitted means any signed-in user, matching
   *  the server, where reads need only a session. */
  permission?: Permission;
  /** Any one of these also grants visibility. Monitoring screens are useful to
   *  more than one role — the queue matters to whoever generates *and* whoever
   *  approves — so a single permission would hide it from half of them. */
  alsoVisibleWith?: Permission[];
}

/**
 * Grouped rather than one flat list of ten: the screens fall into things that
 * *start* work, things that *watch* it, and things that configure the system,
 * and a flat list gave no clue which was which — Generation Queue and Pipeline
 * Executions in particular read as siblings of the screens that feed them.
 *
 * `section` is the heading a group sits under. Groups render in the order
 * their first screen appears here.
 */
const SCREENS: readonly Screen[] = [
  { path: "/dashboard", label: "Dashboard", title: "Generation Operations", section: "Overview" },
  { path: "/reports", label: "📊 Syllabus Reports", title: "Syllabus Spending & Usage Report", section: "Overview" },
  { path: "/users", label: "Users & Audit", section: "Overview", permission: "users:manage" },

  {
    path: "/syllabus",
    label: "Chapters & Topics",
    title: "Syllabus",
    section: "Collections",
    // Visible to anyone with any one of the three hierarchy permissions — a
    // create-only or edit-only role still needs to reach the screen.
    permission: "hierarchy:create",
    alsoVisibleWith: ["hierarchy:edit", "hierarchy:delete"],
  },

  {
    path: "/collections",
    label: "Collections",
    section: "Collections",
    // Left open to any signed-in user on purpose: knowledge sources are
    // uploaded from a collection's page, so an uploader must reach it.
    alsoVisibleWith: [],
  },
  // The pipeline flow gets its own section, above the category flow: running a
  // pipeline and watching its executions are one loop, and separating the two
  // into "generate" and "monitor" groups split a single task across the nav.
  {
    path: "/run-pipeline",
    label: "Run Pipeline",
    title: "Run Pipeline",
    section: "Pipelines",
    permission: "pipeline:run",
  },
  {
    path: "/pipelines",
    label: "Pipeline Executions",
    section: "Pipelines",
    // Watching runs is for whoever can start or approve one. An uploader can
    // do neither, so this was a list they could only look at.
    permission: "pipeline:run",
    alsoVisibleWith: ["content:approve"],
  },

  // The category flow, likewise kept whole: generate, watch the queue, review
  // the output.
  {
    path: "/generation",
    label: "Book In",
    section: "Content Generation",
    permission: "generation:run",
  },
  {
    path: "/book-back-generation",
    label: "Book-Back Generation",
    section: "Content Generation",
    permission: "generation:run",
  },
  {
    path: "/queue",
    label: "Generation Queue",
    section: "Content Generation",
    permission: "generation:run",
    alsoVisibleWith: ["content:approve"],
  },
  {
    path: "/playground",
    label: "🧪 Playground",
    section: "Content Generation",
    permission: "generation:run",
  },

  { path: "/settings", label: "Settings", section: "System", permission: "settings:manage" },
];

// Global fetch wrapper to pass authentication and actor identity headers
const originalFetch = window.fetch;
let currentActiveUser: User | null = null;
let currentToken: string | null = null;

window.fetch = function (input: RequestInfo | URL, init?: RequestInit) {
  const options = init || {};
  const headers = new Headers(options.headers || {});
  if (currentToken) {
    headers.set("Authorization", `Bearer ${currentToken}`);
  }
  // Identity is the bearer token alone. These headers used to carry it, which
  // meant the browser told the server who it was — including its role. They are
  // gone rather than merely ignored, so nothing can start trusting them again.

  return originalFetch(input, { ...options, headers });
};

/**
 * Whether a role may see a screen.
 *
 * Derived from the same ROLE_PERMISSIONS table the server enforces against,
 * rather than its own per-role list of path prefixes. Those lists had to be
 * edited in four places whenever a screen was added, with nothing failing if
 * one was missed.
 *
 * This is presentation only. Hiding a menu item stops nobody from calling the
 * endpoint behind it — every gated action is checked again server-side, which
 * is where access control actually happens.
 */
function isAllowedScreen(path: string, user: User): boolean {
  const screen = SCREENS.find((s) => path.startsWith(s.path));
  if (!screen) return false;
  // No permission listed means "any signed-in user", matching the server,
  // where reads need only a session.
  if (!screen.permission) return true;
  if (can(user.permissions, screen.permission, user.role)) return true;
  return (screen.alsoVisibleWith ?? []).some((p) => can(user.permissions, p, user.role));
}

function Header({
  activeUser,
  onLogout,
}: {
  activeUser: User | null;
  onLogout: () => void;
}) {
  const { pathname } = useLocation();
  const [changingPassword, setChangingPassword] = useState(false);
  const screen = SCREENS.find((s) => s.path === pathname);
  const title =
    screen?.title ??
    screen?.label ??
    (pathname.startsWith("/collections") ? "Collections" : "ZenForge");

  return (
    <div className="top">
      <div>
        <div className="crumb">ZenMarks / AI Content</div>
        <h1>{title}</h1>
      </div>
      <div className="row gap-3 align-center">
        {activeUser && (
          <div className="row gap-2 align-center" style={{ fontSize: "0.85rem" }}>
            <span style={{ opacity: 0.7 }}>Logged in:</span>
            <strong>{activeUser.name}</strong>
            <span className="badge approved" style={{ fontSize: "0.75rem", padding: "2px 6px" }}>
              {activeUser.role}
            </span>
          </div>
        )}
        {activeUser && (
          <button
            className="btn secondary"
            style={{ padding: "4px 10px", fontSize: "0.85rem" }}
            onClick={() => setChangingPassword(true)}
          >
            Change password
          </button>
        )}
        {activeUser && (
          <button
            className="btn secondary"
            style={{ padding: "4px 10px", fontSize: "0.85rem" }}
            onClick={onLogout}
          >
            Logout
          </button>
        )}
      </div>

      {changingPassword && <ChangePassword onClose={() => setChangingPassword(false)} />}
    </div>
  );
}

function MainLayout({
  activeUser,
  onLogout,
}: {
  activeUser: User;
  onLogout: () => void;
}) {
  const allowedScreens = SCREENS.filter((screen) => isAllowedScreen(screen.path, activeUser));

  // Grouped after filtering, not before: a role that can reach none of a
  // section's screens must not be left with its heading floating above
  // nothing.
  const sections: { section: string; screens: typeof allowedScreens }[] = [];
  for (const screen of allowedScreens) {
    const last = sections[sections.length - 1];
    if (last && last.section === screen.section) last.screens.push(screen);
    else sections.push({ section: screen.section, screens: [screen] });
  }

  return (
    <AuthContext.Provider value={activeUser}>
    <div className="app">
      <aside>
        <div className="logo">
          Zen<span>Forge</span>
        </div>
        <div className="nav">
          {sections.map(({ section, screens }) => (
            <div key={section} className="nav-section">
              <div className="nav-heading">{section}</div>
              {screens.map((screen) => (
                <NavLink key={screen.path} to={screen.path}>
                  {screen.label}
                </NavLink>
              ))}
            </div>
          ))}
        </div>
      </aside>

      <main>
        <Header activeUser={activeUser} onLogout={onLogout} />
        <Routes>
          <Route path="/" element={<Navigate to="/dashboard" replace />} />
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/reports" element={<SyllabusReport />} />
          {isAllowedScreen("/collections", activeUser) && (
            <>
              <Route path="/collections" element={<CollectionsList />} />
              <Route path="/collections/:id" element={<CollectionDetail />} />
            </>
          )}
          {isAllowedScreen("/syllabus", activeUser) && (
            <Route path="/syllabus" element={<Syllabus />} />
          )}
          {isAllowedScreen("/generation", activeUser) && (
            <Route path="/generation" element={<Generation />} />
          )}
          {isAllowedScreen("/run-pipeline", activeUser) && (
            <Route path="/run-pipeline" element={<RunPipeline />} />
          )}
          {isAllowedScreen("/book-back-generation", activeUser) && (
            <Route path="/book-back-generation" element={<BookBackGeneration />} />
          )}
          {isAllowedScreen("/playground", activeUser) && (
            <Route path="/playground" element={<Playground />} />
          )}
          {isAllowedScreen("/queue", activeUser) && (
            <Route path="/queue" element={<Queue />} />
          )}
          {isAllowedScreen("/pipelines", activeUser) && (
            <Route path="/pipelines" element={<PipelineExecutions />} />
          )}
          {isAllowedScreen("/users", activeUser) && (
            <Route path="/users" element={<Users />} />
          )}
          {isAllowedScreen("/settings", activeUser) && (
            <Route path="/settings" element={<Settings />} />
          )}
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </main>
    </div>
    </AuthContext.Provider>
  );
}

export function App() {
  const [activeUser, setActiveUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const savedToken = localStorage.getItem("zenforge_auth_token");
    if (savedToken) {
      currentToken = savedToken;
    }

    fetch("/api/v1/auth/me")
      .then((res) => (res.ok ? res.json() : null))
      .then((user: User | null) => {
        if (user) {
          setActiveUser(user);
          currentActiveUser = user;
        } else {
          localStorage.removeItem("zenforge_auth_token");
          currentToken = null;
        }
      })
      .catch(() => null)
      .finally(() => setLoading(false));
  }, []);

  const handleLoginSuccess = (user: User, token: string) => {
    localStorage.setItem("zenforge_auth_token", token);
    localStorage.setItem("zenforge_active_user_id", String(user.id));
    currentToken = token;
    currentActiveUser = user;
    setActiveUser(user);
  };

  const handleLogout = async () => {
    await fetch("/api/v1/auth/logout", { method: "POST" }).catch(() => null);
    localStorage.removeItem("zenforge_auth_token");
    localStorage.removeItem("zenforge_active_user_id");
    currentToken = null;
    currentActiveUser = null;
    setActiveUser(null);
  };

  if (loading) {
    return <div className="loading" style={{ padding: "4rem", textAlign: "center" }}>Initializing ZenForge...</div>;
  }

  return (
    <Router>
      <Routes>
        {!activeUser ? (
          <>
            <Route path="/login" element={<Login onLoginSuccess={handleLoginSuccess} />} />
            <Route path="*" element={<Navigate to="/login" replace />} />
          </>
        ) : (
          <Route
            path="/*"
            element={
              <MainLayout
                activeUser={activeUser}
                onLogout={handleLogout}
              />
            }
          />
        )}
      </Routes>
    </Router>
  );
}
